import { bisectRight } from "d3"
import { EMRData, EMRDataStream, EMRDataStreamId } from "../../../../../Managers/VisualizationManager/ToolBar/Modals/MedicationGraphModalities/Medications"
import { RenderStrategy } from "../Types/Trace"

export type EMRReading = {
    value: number
    time: number
}

// What a medication or flowsheet value means at a time:
// - an infusion reads the rate running then, however long ago it was set;
// - a dose or a flowsheet entry reads the last one at or before then, but only while it is
//   still on screen (`earliestTime`); once it scrolls out of view it expires to "?".
export function getEMRReadingAt(stream: EMRDataStream, renderStrategy: RenderStrategy, time: number, earliestTime: number): EMRReading | undefined {
    const index = bisectRight(stream.times, time) - 1

    if (index < 0) {
        return undefined
    }

    const value = stream.data[index]
    const readingTime = stream.times[index]

    if (value === null || value === undefined || Number.isNaN(value)) {
        return undefined
    }

    if (renderStrategy !== RenderStrategy.INFUSION_MEDICATION && readingTime < earliestTime) {
        return undefined
    }

    return { value, time: readingTime }
}

export function findEMRStream(emrData: EMRData[], dataKey: string): EMRDataStream | undefined {
    const { id, code, units } = JSON.parse(dataKey) as EMRDataStreamId
    const harmonized = emrData.find(data => String(data.id) === String(id))
    return harmonized?.dataStreams.find(stream => stream.code === code && stream.units === units)
}

// The earliest time a dose or flowsheet entry still counts for a reading at `time`. A time on
// screen uses the left edge of the view. A picked time scrolled off screen uses one view width
// before it, which is what the view would show with that time at its right edge.
export function getEarliestEMRTime(time: number, viewStart: number, viewEnd: number): number {
    return time >= viewStart && time <= viewEnd ? viewStart : time - (viewEnd - viewStart)
}

// "1 h 12 min", "35 min", "< 1 min"
export function formatEMRAge(ageMs: number): string {
    const totalMinutes = Math.floor(ageMs / 60_000)

    if (totalMinutes < 1) {
        return "< 1 min"
    }

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    if (hours === 0) {
        return `${minutes} min`
    }

    return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`
}
